import type { MouseEvent } from 'react'
import { toggleEmailButton, toggleMainSiteView } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { selectAccessState } from '@/msgdeskRuntime/state'
import { AvatarImg, BodyContent, Datetime, EmailLink, EmailText, Headline, HeadlineSign, Subject } from './components/Captions/Captions'
import { mainSubjectLineHidden } from './mainMessageData'
import { FoldSubsButton } from '@/msgComponents/controls/FoldSubsButton/Legacy/FoldSubsButton'
import { LockedScreen } from '@/deskUiSkeletons/Blocking/LockedScreen'

// Main message read view (legacy e53-e85). Structural markup is static;
// only the caption leaves subscribe to the store.

function MainHeader() {
  function handleEmailClick(e: MouseEvent<HTMLSpanElement>) {
    toggleEmailButton(e.currentTarget)
  }

  function handleEmailMouseOver(e: MouseEvent<HTMLSpanElement>) {
    toggleEmailButton(e.currentTarget, true, true)
  }

  function handleEmailMouseOut(e: MouseEvent<HTMLSpanElement>) {
    toggleEmailButton(e.currentTarget, true)
  }

  function handleSiteViewClick() {
    toggleMainSiteView()
  }

  return (
    <div {...legacyHookAttrs('main.header', 'md-main-header clear-both mb-[3px] ml-[5px] block min-h-[35px] overflow-hidden')}>
      <div {...legacyHookAttrs('main.avatarFrame', 'md-main-avatar float-left mr-1.5 h-8 w-8 overflow-hidden bg-[silver]')}>
        <AvatarImg
          title="avatar"
          fallback="atts/0fdadbae89ef7d277d6cb443abea43bc?id=avatar.jpg"
          className="md-main-avatar__image block h-8 w-8 object-cover"
          style={{ width: '100%' }}
        />
      </div>
      {/* MAIN TITLE RULE (§0.16 desk identity): message desk -> "@ name_key";
          answer desk -> ":: subject", falling back to its codekey when the
          answer subject is empty. */}
      <span
        {...legacyHookAttrs('main.nameKeySign', 'md-main-name-sign float-left cursor-pointer bg-white pr-0 text-[21px] font-bold leading-8 text-[#3472a9]')}
        onClick={handleEmailClick}
        onMouseOver={handleEmailMouseOver}
        onMouseOut={handleEmailMouseOut}
      >
        <HeadlineSign />
      </span>
      <span
        {...legacyHookAttrs('main.authorFrame', 'md-main-author float-left clear-none bg-white text-[21px] font-bold leading-8 text-[#3472a9]')}
      >
        <Headline fallback="Nazarov" />
      </span>
      <div {...legacyHookAttrs('main.emailRegion', 'md-main-email-region hidden clear-both')}>
        <span
          {...legacyHookAttrs('main.siteView', 'md-main-site-view float-left mr-[3px] hidden cursor-pointer bg-[#f4f5f6] text-[10px]')}
          onClick={handleSiteViewClick}
        >
          Siteview ON
        </span>
        <span {...legacyHookAttrs('main.emailText', 'md-main-email-text float-left cursor-pointer')}>
          <EmailText fallback="The email address of this message: " />
        </span>
        <EmailLink
          fallbackHref="mailto:iegyc7j70k27@msgdsk.com"
          fallbackText="iegyc7j70k27@msgdsk.com"
          className="md-main-email-link"
        />
      </div>
    </div>
  )
}

function SubjectBar() {
  // LEGACY HEADLINE RULE (php page.php:95): the small subject line hides on
  // answer desks (the subject already IS the big title there) and on empty
  // subjects. Mounted either way — main.subjectLine is a legacy hook — and
  // the datetime beside it always stays.
  const { state } = useSubsStore()
  const subjectLineHidden = mainSubjectLineHidden(state)
  return (
    <div {...legacyHookAttrs('main.subjectRegion', 'md-main-subject relative mb-[3px] ml-[-5px] block bg-[#eceef5] pl-[5px]')}>
      <div
        {...legacyHookAttrs('main.subjectLine', 'md-main-subject-line ml-0')}
        style={subjectLineHidden ? { display: 'none' } : undefined}
      >
        <span {...legacyHookAttrs('main.subjectText', 'md-main-subject-text ml-[5px] mr-40 block whitespace-nowrap p-0 text-xs text-[#3472a9]')}>
        <Subject fallback="My personal page" />
        </span>
      </div>
      <div {...legacyHookAttrs('main.lineClear', 'md-main-line-clear clear-both ml-[5px] block h-px leading-px')} />
      <span {...legacyHookAttrs('main.datetime', 'md-main-datetime absolute right-0 top-1 float-left m-0 bg-[#eceef5] py-0 pl-[7px] pr-[5px] text-xs font-normal text-[#3472a9]')}>
        <Datetime fallback="Thu 19 Sep 2024 19:42" />
      </span>
    </div>
  )
}

function MessageBody() {
  return (
    <div {...legacyHookAttrs('main.body', 'md-main-body relative m-0 block clear-both')}>
      <FoldSubsButton kind="msg" label="Fold msg" />
      <div {...legacyHookAttrs('main.iframe', 'md-main-content m-0 min-h-[52px] bg-white px-5 py-[15px] text-[13px] font-[Arial,Tahoma,Verdana,sans-serif]')}>
        <BodyContent>
          <div>
            <p>
              <span style={{ fontSize: 'xx-large' }}>
                <em>
                  <strong>
                    <span style={{ fontFamily: 'times new roman, times' }}>Welcome to my desk!!!!!</span>
                  </strong>
                </em>
              </span>
            </p>
          </div>
        </BodyContent>
      </div>
    </div>
  )
}

export function MainMessage() {
  const { state } = useSubsStore()
  const access = selectAccessState(state)
  const isVisible = state.renderProjection.mode === 'msg'

  return (
    // SKELETON: main.* contracts are the legacy read-view addresses.
    // Keep hooks on the same semantic parts when redesigning the message.
    <div
      // S1: the `hidden` class mirrors projectChromeState's hidden-contract
      // treatment so the SSR markup matches the post-adoption DOM exactly.
      {...legacyHookAttrs('main.root', `md-main-message std_rich m-0 bg-white pl-0${isVisible ? '' : ' hidden'}`)}
      style={{ display: isVisible ? 'block' : 'none' }}
    >
      {/* LOCKED-SCREEN: a show/time-locked desk shows ONLY the locked
          template — no header, subject bar, card chrome, or edit mount (the
          locked notice already carries @codekey, namekey, and subject).
          Same tolerance the LOCKED task established for absent main.* nodes. */}
      {access.showUnlocked ? (
        <>
          <MainHeader />
          <div {...legacyHookAttrs('main.quad', 'md-main-card clear-both ml-[5px] mr-0 block bg-[#f5f5f5]')}>
            <SubjectBar />
            <MessageBody />
            <div {...legacyHookAttrs('main.editMount', 'md-main-edit-mount hidden clear-both overflow-hidden border-t border-[#eaedf6] pb-[5px] pl-[5px]')} />
          </div>
        </>
      ) : (
        <LockedScreen className="bg-white" />
      )}
    </div>
  )
}
